<template>
    <div class="tw:rounded-md tw:border tw:border-gray-300 tw:p-4">
        <audio
            ref="audioRef"
            :src="src"
            preload="metadata"
            @loadedmetadata="onLoadedMetadata"
            @timeupdate="onTimeUpdate"
            @play="$emit('play')"
            @pause="$emit('pause')"
            @ended="$emit('ended')"
        >
            <track
                kind="captions"
                srclang="en"
                label="English captions"
                :src="captionsTrackSrc"
                default
            >
        </audio>

        <div class="tw:mb-3 tw:flex tw:items-center tw:gap-4">
            <e-button
                type="is-primary"
                size="is-small"
                :icon-left="isPlaying ? 'pause' : 'play'"
                @click="togglePlayback"
            >
                {{ isPlaying ? 'Pause' : 'Play' }}
            </e-button>

            <div class="tw:text-sm tw:text-gray-600">
                {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
            </div>
        </div>

        <div class="tw:mb-3">
            <input
                type="range"
                min="0"
                :max="Math.max(duration, 0)"
                step="1"
                :value="currentTime"
                class="tw:w-full"
                @input="onSeek"
            >
        </div>

        <div class="tw:grid tw:grid-cols-[repeat(auto-fit,minmax(180px,1fr))] tw:gap-3">
            <label class="tw:flex tw:flex-col tw:gap-1 tw:text-sm">
                <span>Speed</span>
                <select v-model.number="playbackRate" class="tw:w-full">
                    <option v-for="rate in rates" :key="rate" :value="rate">
                        {{ rate }}x
                    </option>
                </select>
            </label>

            <label class="tw:flex tw:flex-col tw:gap-1 tw:text-sm">
                <span>Volume</span>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    :value="volume"
                    class="tw:w-full"
                    @input="onVolumeChange"
                >
            </label>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AudioPlayer',

    props: {
        src: {
            type: String,
            required: true,
        },
        initialPosition: {
            type: Number,
            required: false,
            default: 0,
        },
    },

    emits: ['timeupdate', 'ended', 'play', 'pause'],

    data: () => ({
        isPlaying: false,
        currentTime: 0,
        duration: 0,
        playbackRate: 1,
        volume: 1,
        rates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        captionsTrackSrc:
            'data:text/vtt;charset=utf-8,WEBVTT%0A%0A00%3A00.000%20--%3E%2000%3A00.500%0ANo%20captions%20available',
    }),

    watch: {
        playbackRate(value) {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            audio.playbackRate = value;
        },
    },

    methods: {
        togglePlayback() {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            if (audio.paused) {
                audio.play();
                this.isPlaying = true;
                return;
            }

            audio.pause();
            this.isPlaying = false;
        },

        onLoadedMetadata() {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            this.duration = Number.isFinite(audio.duration) ? Math.floor(audio.duration) : 0;
            if (this.initialPosition > 0) {
                audio.currentTime = this.initialPosition;
                this.currentTime = Math.floor(this.initialPosition);
            }
        },

        onTimeUpdate() {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            this.currentTime = Math.floor(audio.currentTime || 0);
            this.duration = Number.isFinite(audio.duration) ? Math.floor(audio.duration) : this.duration;

            this.$emit('timeupdate', {
                currentTime: this.currentTime,
                duration: this.duration,
            });
        },

        onSeek(event) {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            const value = Number(event.target.value);
            audio.currentTime = value;
            this.currentTime = Math.floor(value);
        },

        onVolumeChange(event) {
            const audio = this.$refs.audioRef;
            if (!audio) return;

            const value = Number(event.target.value);
            audio.volume = value;
            this.volume = value;
        },

        formatTime(seconds) {
            const totalSeconds = Math.max(0, Math.floor(seconds || 0));
            const minutes = Math.floor(totalSeconds / 60);
            const remaining = totalSeconds % 60;

            return `${minutes}:${String(remaining).padStart(2, '0')}`;
        },
    },
};
</script>
